import { LinkButton } from '@pmi/dsm-react';
import { useMemo } from 'react';
import { HeartOnIcon } from '@pmi/dsm-react/dist/components/icons/fa/16';
import { Col, Row } from 'react-bootstrap';
import ResponsiveTable from '../../shared/components/ResponsiveTable';
import {
  AscendingSortDirection,
  HeaderPropSortCallback,
  SortedColumn,
} from '../../shared/components/ResponsiveTable/models';
import { AccountSelectDto } from './models';
import styles from './accountselect.module.scss';
import displayAccountName from '../../shared/utils/displayAccountName';

interface TableDataProps {
  data: AccountSelectDto[];
  handleViewAccount: any;
  handleViewAgreement: any;
  handleRemoveFromFavorites: any;
  onTableSort: HeaderPropSortCallback;
  sortedColumn: SortedColumn | null;
}

const getColumns = (
  handleViewAccount: any,
  handleViewAgreement: any,
  handleRemoveFromFavorites: any,
  enableSorting: boolean,
  sortedColumn: SortedColumn,
) =>
  [
    {
      name: 'name',
      title: 'Account Name',
      sortingEnabled: enableSorting,
      direction: sortedColumn?.direction
        ? sortedColumn?.direction
        : AscendingSortDirection.toString(),
      cell: (cntx: {
        row: {
          original: {
            logoUrl: string;
            name: string;
            originalName: string;
            id: string;
            engagements: string[];
          };
        };
      }) => {
        const {
          name,
          originalName,
          logoUrl,
          id,
          engagements = [],
        } = cntx.row.original;
        const clone = [...engagements];
        if (clone && clone.length > 0) {
          clone.sort();
        }
        return (
          <Row>
            <Col xs={2}>
              <img
                className={`${styles.logoContainer}`}
                alt=""
                src={
                  logoUrl ??
                  'https://upload.wikimedia.org/wikipedia/commons/3/3f/Placeholder_view_vector.svg'
                }
              />
            </Col>
            <Col className={`${styles.labelContainer} ms-2`}>
              <LinkButton
                className={`${styles.accountName} btn-link text-start p-0 m-0`}
                variant="light"
                key={`handleViewAccount${id}-${new Date().toTimeString()}`}
                titleText={displayAccountName(name, originalName)}
                onClick={handleViewAccount(id)}
              />
              <div
                className={`d-block flex-grow-1 mb-3 ${styles.engagementType}`}
              >
                {clone.length > 0 &&
                  clone?.map((e: string, index: number) => (
                    <span
                      key={`handleViewAccount${index.toString()}-${new Date().toTimeString()}`}
                    >
                      <span className="d-inline-block">
                        {e.replace('Org', 'Organizational')}
                      </span>
                      <span className="d-inline-block me-1">
                        {clone.length > 1 && index < clone.length - 1
                          ? ','
                          : ''}
                      </span>
                    </span>
                  ))}
              </div>
            </Col>
          </Row>
        );
      },
      width: 25,
    },
    {
      name: 'primaryContacts',
      title: 'Point of Contact',
      sortingEnabled: false,
      cell: (cntx) => {
        // values.sort((one, two) => (one > two ? -1 : 1));
        const contacts: string[] = cntx.row.original.primaryContacts.split(',');
        if (contacts.length > 1) {
          contacts.sort((one, two) => (one > two ? 1 : -1));
        }
        return <span data-exclude-lg>{contacts.join(', ')}</span>;
      },
      width: 15,
    },
    {
      name: 'Agreement ID(s)',
      title: 'Agreement ID(s)',
      sortingEnabled: false,
      cell: (cntx) => {
        const { deals, payByUserDeals } = cntx.row.original;
        const dealIds =
          deals
            ?.map((id: string) => id)
            ?.filter((id: string) => id.length > 0) || [];
        return (
          <div className={styles.accountAgreementsBlock}>
            {dealIds.map((id: string, index: number) => (
              <span key={`link-${id}`} className={styles.linkAgreementBox}>
                <LinkButton
                  titleText={id}
                  onClick={handleViewAgreement(cntx.row.original.id, id)}
                  size="sm"
                  className={styles.linkAgreement}
                />
                {payByUserDeals.includes(id) && <span> (Pay by User)</span>}
                {index < dealIds.length - 1 ? (
                  <span dangerouslySetInnerHTML={{ __html: ',&nbsp;' }} />
                ) : (
                  ' '
                )}
              </span>
            ))}
          </div>
        );
      },
      width: 10,
    },
    {
      name: '   ',
      title: '',
      sortingEnabled: false,
      cell: (cntx) => (
        <div className="d-flex">
          <LinkButton
            className={styles.favorite}
            variant="light"
            size="md"
            key={`handleRemoveFromFavorites-${cntx.row.original.id}`}
            titleText=""
            onClick={handleRemoveFromFavorites(cntx.row.original)}
            icon={HeartOnIcon}
          />
        </div>
      ),
      width: 1,
    },
  ] as SortedColumn[];
export default function AccountSelectFavoritesTable({
  data,
  handleViewAccount,
  handleViewAgreement,
  handleRemoveFromFavorites,
  onTableSort,
  sortedColumn,
}: TableDataProps) {
  const columns = useMemo(
    () =>
      getColumns(
        handleViewAccount,
        handleViewAgreement,
        handleRemoveFromFavorites,
        data.length > 0,
        sortedColumn,
      ),
    [],
  );

  return (
    <div className={styles.accountSelectTable} data-testid="table-power-user">
      <ResponsiveTable
        data={data}
        paginationProps={{
          page: 1,
          showPageSizes: false,
          globalPageSize: true,
        }}
        columns={columns}
        showPagination={false}
        onSort={onTableSort}
      />
      {data.length === 0 && <h4 className="pt-3 text-center">No favorites</h4>}
    </div>
  );
}
